import React, { useCallback, useMemo } from 'react'
import classnames from 'classnames/bind'
import { AutomationTarget } from '../../../types'
import { getCssColorFromColorName } from '../../../utils/colors'
import { ColorNames } from '../../../constants/colorVars'
import Icon, { IconStyle } from '../icon'
import type { IconProps } from '../icon'
import styles from './styles.scss'

const cx = classnames.bind(styles)

/**
 * InfoPill is a small, pill-shaped container that can display a small amount of information e.g status.
 *
 * The Icon can be specified by name (iconName) for simple solid icons colored based on the foreground color.
 * Optionally you can pass iconColor as well (which only applies to iconName approach)
 * ALTERNATIVELY the icon can be passed as a component (icon) for more complex icons.
 */
export interface InfoPillProps extends AutomationTarget {
  iconName?: string
  iconColor?: ColorNames
  icon?: React.FC<IconProps> | React.ReactNode
  iconOnRight?: boolean
  content?: React.ReactNode
  size?: 'x-small' | 'small' | 'medium'
  backgroundColor?: ColorNames
  foregroundColor?: ColorNames
  includeBorder?: boolean
  className?: string
  style?: React.CSSProperties
  extraPadding?: 'small' | 'medium'
  fontWeight?: 'normal' | 'bold'
  label?: React.ReactNode
  iconStyle?: IconStyle
}

const InfoPill: React.FC<InfoPillProps> = ({
  iconName,
  iconColor,
  icon,
  iconOnRight = false,
  content,
  elmId = 'info-pill',
  size = 'small',
  backgroundColor = ColorNames.OffWhiteDark,
  foregroundColor = ColorNames.Gray,
  includeBorder = false,
  className,
  style,
  extraPadding,
  fontWeight = 'bold',
  label,
  iconStyle = 'solid',
}) => {
  // extract raw color values from color names
  const backgroundColorRaw = getCssColorFromColorName(backgroundColor)
  const foregroundColorRaw = getCssColorFromColorName(foregroundColor)

  const colorStyles = useMemo(
    () => ({
      backgroundColor: backgroundColorRaw,
      color: foregroundColorRaw,
      border: includeBorder ? `1px solid ${foregroundColorRaw}` : 'none',
    }),
    [backgroundColorRaw, foregroundColorRaw, includeBorder]
  )

  // get icon component if iconName is provided
  const _icon: React.ReactNode = useMemo(
    () =>
      iconName ? (
        <Icon
          name={iconName}
          color={iconColor || 'inherit'}
          iconStyle={iconStyle}
          fitted
        />
      ) : (
        icon
      ),
    [iconName, iconColor, icon]
  )

  const renderPill = useCallback(
    () => (
      <div
        className={cx(
          'info-pill',
          size,
          { 'extra-padding-small': extraPadding === 'small' },
          { 'extra-padding-medium': extraPadding === 'medium' },
          { 'font-weight-normal': fontWeight === 'normal' },
          className
        )}
        style={{ ...colorStyles, ...style }}
        data-elm-id={elmId || 'info_pill'}
      >
        {!iconOnRight && _icon}
        {content}
        {iconOnRight && _icon}
      </div>
    ),
    [
      size,
      extraPadding,
      fontWeight,
      className,
      colorStyles,
      style,
      elmId,
      iconOnRight,
      _icon,
      content,
    ]
  )

  return (
    <>
      {label ? (
        <div data-elm-id={`${elmId}-container`} className={cx('container')}>
          {renderPill()}
          <div className={cx('label')}>{label}</div>
        </div>
      ) : (
        renderPill()
      )}
    </>
  )
}

interface GroupProps {
  className?: string
  style?: React.CSSProperties
  children?: React.ReactNode
}

const Group: React.FC<GroupProps> = ({ className, children, style }) => (
  <div className={cx('info-pill-group', className)} style={style}>
    {children}
  </div>
)

interface InfoPillStatic {
  Group: typeof Group
}

const InfoPillWithStatic: React.FC<InfoPillProps> & InfoPillStatic =
  InfoPill as React.FC<InfoPillProps> & InfoPillStatic

InfoPillWithStatic.Group = Group

export default InfoPillWithStatic
